import { z } from 'zod';

export const COLOR_RULE_TYPES = ['city', 'frequency', 'visit_type', 'team', 'first_visit', 'tag'] as const;
export type ColorRuleType = (typeof COLOR_RULE_TYPES)[number];
export const COLOR_PALETTE = [
  '#7f1d1d',
  '#991b1b',
  '#b91c1c',
  '#c2410c',
  '#9a3412',
  '#854d0e',
  '#713f12',
  '#4d7c0f',
  '#365314',
  '#166534',
  '#15803d',
  '#065f46',
  '#047857',
  '#115e59',
  '#0f766e',
  '#155e75',
  '#0e7490',
  '#075985',
  '#0369a1',
  '#1e40af',
  '#1d4ed8',
  '#312e81',
  '#4338ca',
  '#4c1d95',
  '#6d28d9',
  '#581c87',
  '#7e22ce',
  '#86198f',
  '#a21caf',
  '#9d174d',
  '#be185d',
  '#9f1239',
] as const;
export const NEUTRAL_COLOR = '#475569';
export function normalizeColorValue(value: string): string {
  return value.normalize('NFKC').trim().replace(/\s+/g, ' ').toLowerCase();
}
export const colorValueSchema = z.string().max(100).transform(normalizeColorValue).pipe(z.string().min(1).max(100));
const valueSchema = z
  .object({
    key: colorValueSchema,
    label: z.string().trim().min(1).max(100),
    color: z
      .string()
      .regex(/^#[0-9a-fA-F]{6}$/)
      .transform((v) => v.toLowerCase()),
  })
  .strict();
export const scheduleColorsSchema = z
  .object({
    crewEnabled: z.boolean(),
    rules: z
      .array(
        z
          .object({
            type: z.enum(COLOR_RULE_TYPES),
            enabled: z.boolean(),
            values: z.array(valueSchema).max(100),
          })
          .strict()
          .superRefine((rule, ctx) => {
            if (new Set(rule.values.map((v) => v.key)).size !== rule.values.length)
              ctx.addIssue({ code: 'custom', message: 'Duplicate values.' });
          }),
      )
      .max(3),
  })
  .strict()
  .superRefine((settings, ctx) => {
    if (new Set(settings.rules.map((r) => r.type)).size !== settings.rules.length)
      ctx.addIssue({ code: 'custom', message: 'Duplicate rules.' });
  });
export type ScheduleColors = z.infer<typeof scheduleColorsSchema>;
export const DEFAULT_SCHEDULE_COLORS: ScheduleColors = { crewEnabled: false, rules: [] };
export type ColorFacts = Record<ColorRuleType, string[]>;
export interface ColorSignal {
  type: ColorRuleType;
  key: string;
  label: string;
  color: string;
}
export interface VisitColors {
  primary: ColorSignal | null;
  indicators: ColorSignal[];
  facts: ColorFacts;
  legend?: ColorSignal[];
}
/**
 * A saved team carries its own color. When the team rule is on, any team the
 * rule does not list explicitly is colored with its saved color and name, so
 * turning on "color by team" works without retyping every team.
 */
export function withSavedTeamColors(
  settings: ScheduleColors,
  teams: readonly { id: string; name: string; color: string }[],
): ScheduleColors {
  if (!teams.length || !settings.rules.some((r) => r.type === 'team' && r.enabled)) return settings;
  return {
    ...settings,
    rules: settings.rules.map((rule) => {
      if (rule.type !== 'team' || !rule.enabled) return rule;
      const listed = new Set(rule.values.map((v) => v.key));
      const extra = teams
        .filter((team) => /^#[0-9a-fA-F]{6}$/.test(team.color) && !listed.has(normalizeColorValue(team.id)))
        .map((team) => ({
          key: normalizeColorValue(team.id),
          label: team.name.trim().slice(0, 100) || team.id,
          color: team.color.toLowerCase(),
        }));
      return { ...rule, values: [...rule.values, ...extra] };
    }),
  };
}
export function resolveVisitColors(settings: ScheduleColors, facts: ColorFacts): VisitColors {
  const signals = settings.rules
    .filter((r) => r.enabled)
    .flatMap((rule) => {
      // Tags can match several values. Configuration order is the stable tie breaker.
      const match = rule.values.find((v) => facts[rule.type].includes(v.key));
      if (match) return [{ ...match, type: rule.type }];
      if (rule.type === 'city') return [{ type: rule.type, key: '', label: '', color: NEUTRAL_COLOR }];
      return [];
    });
  const legend = settings.rules
    .filter((r) => r.enabled)
    .flatMap((rule) =>
      rule.values.filter((v) => facts[rule.type].includes(v.key)).map((v) => ({ ...v, type: rule.type })),
    );
  return { primary: signals[0] ?? null, indicators: signals.slice(1, 3), facts, legend };
}
export function colorMatches(colors: VisitColors | undefined, filter: string): boolean {
  if (!filter) return true;
  const split = filter.indexOf(':');
  const type = filter.slice(0, split) as ColorRuleType;
  if (type === 'city' && filter.slice(split + 1) === '')
    return (
      (colors?.primary?.type === 'city' && colors.primary.key === '') ||
      !!colors?.indicators.some((v) => v.type === 'city' && v.key === '')
    );
  return split > 0 && !!colors?.facts[type]?.includes(filter.slice(split + 1));
}
export const COLOR_COPY = {
  en: {
    title: 'Calendar colors',
    ruleType: 'Color by',
    city: 'City or town',
    frequency: 'Frequency',
    visit_type: 'Visit type',
    team: 'Team',
    first_visit: 'First visit',
    tag: 'Tag',
    unknown: 'Unassigned towns',
    legend: 'Color legend and filters',
    all: 'All color values',
    crew: 'Show colors to crew',
    save: 'Save colors',
    addRule: 'Add rule',
    moveUp: 'Move rule up',
    moveDown: 'Move rule down',
    addValue: 'Add value',
    teamHint: 'Each team uses the color saved on the team unless you choose a different one here.',
    team_pick: 'Team',
    team_choose: 'Choose a team',
    team_inactive: 'Inactive team',
    teamsFailed: 'Could not load teams. Reload the page to try again.',
    key: 'Value',
    label: 'Label',
    color: 'Color',
    remove: 'Remove',
    enabled: 'Enabled',
    loading: 'Loading colors',
    saved: 'Colors saved',
    failed: 'Could not save. Reconnect or reload and try again.',
    stale: 'Colors changed. Reload before saving.',
    reload: 'Reload colors',
    hint: 'Order: primary fill, then two labeled indicators. First visit uses the earliest active or completed visit across this customer’s history. Canceled and missed visits do not count.',
    first: 'New',
    tags: 'Visit tags',
    tagHint: 'Separate tags with commas. These labels are internal.',
    saveTags: 'Save tags',
    reloadTags: 'Reload tags',
    tagsFailed: 'Could not save tags. Reload tags and try again.',
    tagsStale: 'Tags changed. Reload tags before saving.',
    tagsLoadFailed: 'Could not load tags. Reload tags to try again.',
  },
  es: {
    title: 'Colores del calendario',
    ruleType: 'Colorear por',
    city: 'Ciudad o localidad',
    frequency: 'Frecuencia',
    visit_type: 'Tipo de visita',
    team: 'Equipo',
    first_visit: 'Primera visita',
    tag: 'Etiqueta',
    unknown: 'Localidades sin asignar',
    legend: 'Leyenda de colores y filtros',
    all: 'Todos los valores de color',
    crew: 'Mostrar colores al personal',
    save: 'Guardar colores',
    addRule: 'Agregar regla',
    moveUp: 'Subir regla',
    moveDown: 'Bajar regla',
    addValue: 'Agregar valor',
    teamHint: 'Cada equipo usa el color guardado en el equipo, a menos que usted elija otro aquí.',
    team_pick: 'Equipo',
    team_choose: 'Elija un equipo',
    team_inactive: 'Equipo inactivo',
    teamsFailed: 'No se pudieron cargar los equipos. Vuelva a cargar la página para intentarlo de nuevo.',
    key: 'Valor',
    label: 'Nombre',
    color: 'Color',
    remove: 'Eliminar',
    enabled: 'Activada',
    loading: 'Cargando colores',
    saved: 'Colores guardados',
    failed: 'No se pudo guardar. Vuelva a conectarse o cargar e inténtelo de nuevo.',
    stale: 'Los colores cambiaron. Vuelva a cargar antes de guardar.',
    reload: 'Volver a cargar colores',
    hint: 'Orden: color principal y luego dos indicadores con nombre. La primera visita es la visita activa o completada más temprana del historial del cliente. Las visitas canceladas y las ausencias no cuentan.',
    first: 'Nuevo',
    tags: 'Etiquetas de la visita',
    tagHint: 'Separe las etiquetas con comas. Estos nombres son internos.',
    saveTags: 'Guardar etiquetas',
    reloadTags: 'Volver a cargar etiquetas',
    tagsFailed: 'No se pudieron guardar las etiquetas. Vuelva a cargarlas e inténtelo de nuevo.',
    tagsStale: 'Las etiquetas cambiaron. Vuelva a cargarlas antes de guardar.',
    tagsLoadFailed: 'No se pudieron cargar las etiquetas. Vuelva a cargarlas para intentarlo de nuevo.',
  },
} as const;
/** Choose the WCAG AA readable foreground for arbitrary owner colors. */
export function colorForeground(hex: string): '#000000' | '#ffffff' {
  const channels = [1, 3, 5]
    .map((i) => parseInt(hex.slice(i, i + 2), 16) / 255)
    .map((c) => (c <= 0.04045 ? c / 12.92 : ((c + 0.055) / 1.055) ** 2.4));
  const luminance = channels[0]! * 0.2126 + channels[1]! * 0.7152 + channels[2]! * 0.0722;
  return (luminance + 0.05) / 0.05 >= 1.05 / (luminance + 0.05) ? '#000000' : '#ffffff';
}
